import { useCallback, useEffect, useRef } from 'react';
import { useTripleClick } from './useTripleClick';
import { useTripleTouch } from './useTripleTouch';

interface UsePinTriggerOptions {
  onTrigger: () => void;
  excludeSelectors?: string[];
  clickDelay?: number;
}

/**
 * Combined hook that detects both 3-click and 3-finger touch
 * Triggers the callback when either is detected
 */
export const usePinTrigger = (options: UsePinTriggerOptions) => {
  const { onTrigger, excludeSelectors = [], clickDelay = 500 } = options;
  const isTriggered = useRef(false);
  const triggerCooldown = useRef<number>(0);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const COOLDOWN_DURATION = 2000; // 2 seconds cooldown to prevent multiple triggers

  // Prevent multiple rapid triggers
  const safeTrigger = useCallback(() => {
    const now = Date.now();
    
    // Check cooldown
    if (now - triggerCooldown.current < COOLDOWN_DURATION) {
      return;
    }
    
    // Check if already triggered (prevent double triggers)
    if (isTriggered.current) {
      return;
    }
    
    isTriggered.current = true;
    triggerCooldown.current = now;
    
    // Reset trigger flag after a short delay
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => {
      isTriggered.current = false;
    }, 1000);
    
    // Call the trigger callback
    onTrigger();
  }, [onTrigger]);

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, []);

  // 3-click detection
  const { handleClick, reset: resetClick } = useTripleClick({
    delay: clickDelay,
    onTripleClick: safeTrigger,
  });

  // 3-finger touch detection
  const { 
    handleTouchStart, 
    handleTouchEnd, 
    handleTouchMove, 
    reset: resetTouch 
  } = useTripleTouch({
    onTripleTouch: safeTrigger,
    excludeSelectors,
  });

  // Detect if device supports touch
  const isTouchDevice = useRef<boolean>(false);
  
  useEffect(() => {
    // Check if device supports touch
    isTouchDevice.current = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }, []);

  // Set up global event listeners
  useEffect(() => {
    // Mouse click listener - only for non-touch devices (desktop/laptop)
    const handleGlobalClick = (event: MouseEvent) => {
      // Only handle clicks on non-touch devices
      // On touch devices, clicks are often generated after touch events, so we ignore them
      if (isTouchDevice.current) {
        return; // Skip click handling on touch devices
      }
      
      // Skip if clicking on excluded elements
      if (excludeSelectors.some(selector => {
        const target = event.target as Element;
        return target?.matches?.(selector) || target?.closest?.(selector);
      })) {
        return;
      }
      
      handleClick(event);
    };

    // Touch event listeners - only for touch devices
    const handleGlobalTouchStart = (event: TouchEvent) => {
      // Only process if it's a touch event and device supports touch
      if (event.touches && event.touches.length > 0 && isTouchDevice.current) {
        handleTouchStart(event);
      }
    };

    const handleGlobalTouchEnd = (event: TouchEvent) => {
      if (isTouchDevice.current) {
        handleTouchEnd(event);
      }
    };

    const handleGlobalTouchMove = (event: TouchEvent) => {
      if (isTouchDevice.current) {
        handleTouchMove(event);
      }
    };

    // Add event listeners
    document.addEventListener('click', handleGlobalClick, true); // Use capture phase
    document.addEventListener('touchstart', handleGlobalTouchStart, { passive: true });
    document.addEventListener('touchend', handleGlobalTouchEnd, { passive: true });
    document.addEventListener('touchmove', handleGlobalTouchMove, { passive: true });

    // Cleanup
    return () => {
      document.removeEventListener('click', handleGlobalClick, true);
      document.removeEventListener('touchstart', handleGlobalTouchStart);
      document.removeEventListener('touchend', handleGlobalTouchEnd);
      document.removeEventListener('touchmove', handleGlobalTouchMove);
      resetClick();
      resetTouch();
    };
  }, [handleClick, handleTouchStart, handleTouchEnd, handleTouchMove, excludeSelectors, resetClick, resetTouch]);

  return {
    reset: () => {
      resetClick();
      resetTouch();
      isTriggered.current = false;
      triggerCooldown.current = 0;
    },
  };
};

